import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import {
  listRisks,
  createRisk,
  updateRisk,
  deleteRisk,
  importSeed,
} from '../../lib/api/adminApi';
import { login, logout, isAuthenticated } from '../../lib/api/authApi';
import AdminDashboard from './AdminDashboard';
import AdminMessages from './AdminMessages';
import AdminRescue from './AdminRescue';
import AdminReviews from './AdminReviews';
import AdminProToggle from './AdminProToggle';

const RISK_TYPES = ['delisted', 'always_online', 'server_risk', 'safe'];
const COLUMNS = [
  { key: 'steamAppId', label: 'col_app' },
  { key: 'name', label: 'col_name' },
  { key: 'riskType', label: 'col_type' },
  { key: 'severity', label: 'col_sev' },
  { key: 'gogUrl', label: 'col_gog' },
  { key: 'notes', label: 'col_notes' },
];
const EMPTY = {
  steamAppId: '',
  name: '',
  riskType: 'server_risk',
  severity: 1,
  source: '',
  notes: '',
  gogUrl: '',
};

/* ---------------- Login ---------------- */
function AdminLogin({ onSuccess }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(username, password);
      onSuccess();
    } catch {
      setError(t('admin.login_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <form className="admin-login__card" onSubmit={submit}>
        <div className="admin-login__mark">◆</div>
        <h1 className="admin-login__title">{t('admin.login_title')}</h1>
        <p className="admin-login__sub">{t('admin.login_sub')}</p>
        <input
          type="text"
          placeholder={t('admin.login_user')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          required
        />
        <div className="admin-login__pass">
          <input
            type={showPass ? 'text' : 'password'}
            placeholder={t('admin.login_pass')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="button"
            className="admin-login__eye"
            onClick={() => setShowPass((v) => !v)}
            aria-label={showPass ? t('admin.hide_pass') : t('admin.show_pass')}
            title={showPass ? t('admin.hide_pass') : t('admin.show_pass')}
            tabIndex={-1}
          >
            {showPass ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
        {error && <p className="admin-login__error">{error}</p>}
        <button type="submit" className="btn btn--amber" disabled={loading}>
          {loading ? '…' : t('admin.login_btn')}
        </button>
        <a className="admin-login__back" href="/">
          {t('admin.back')}
        </a>
      </form>
    </div>
  );
}

/* ---------------- Formulaire d'édition ---------------- */
function RiskForm({ mode, data, onField, onSubmit, onCancel, error }) {
  const { t } = useTranslation();
  return (
    <div className="risk-form__overlay" onClick={onCancel}>
      <form className="risk-form" onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <h2>{mode === 'new' ? t('admin.form_new') : t('admin.form_edit')}</h2>

        <div className="risk-form__grid">
          <label>
            {t('admin.f_app')}
            <input
              type="number"
              value={data.steamAppId}
              onChange={onField('steamAppId')}
              disabled={mode === 'edit'}
              required
            />
          </label>
          <label>
            {t('admin.f_type')}
            <select value={data.riskType} onChange={onField('riskType')}>
              {RISK_TYPES.map((rt) => (
                <option key={rt} value={rt}>
                  {t(`risk.${rt}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="risk-form__wide">
            {t('admin.f_name')}
            <input type="text" value={data.name} onChange={onField('name')} required />
          </label>
          <label>
            {t('admin.f_sev')}
            <input
              type="number"
              min="0"
              max="3"
              value={data.severity}
              onChange={onField('severity')}
            />
          </label>
          <label>
            {t('admin.f_source')}
            <input type="text" value={data.source} onChange={onField('source')} />
          </label>
          <label className="risk-form__wide">
            {t('admin.f_gog')}
            <input
              type="url"
              value={data.gogUrl}
              onChange={onField('gogUrl')}
              placeholder="https://www.gog.com/game/..."
            />
          </label>
          <label className="risk-form__wide">
            {t('admin.f_notes')}
            <textarea rows="3" value={data.notes} onChange={onField('notes')} />
          </label>
        </div>

        {error && <p className="risk-form__error">{error}</p>}

        <div className="risk-form__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            {t('admin.cancel')}
          </button>
          <button type="submit" className="btn btn--amber">
            {t('admin.save')}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Page ---------------- */
function AdminPage() {
  const { t } = useTranslation();
  const [authed, setAuthed] = useState(isAuthenticated());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'severity', dir: 'desc' });
  const [view, setView] = useState('risks');
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Renvoie true si l'erreur etait un 401 (session expiree) -> retour au login.
  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      logout();
      setAuthed(false);
      return true;
    }
    return false;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await listRisks();
      setEntries(data);
      setError(null);
    } catch (err) {
      if (!handleAuthError(err)) setError(t('admin.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = entries.filter(
      (e) =>
        (filter === 'all' || e.riskType === filter) &&
        (!q || (e.name || '').toLowerCase().includes(q))
    );
    if (sort.key) {
      list = [...list].sort((a, b) => {
        let va = a[sort.key];
        let vb = b[sort.key];
        if (sort.key === 'gogUrl') {
          va = va ? 1 : 0;
          vb = vb ? 1 : 0;
        }
        let cmp;
        if (typeof va === 'number' || typeof vb === 'number') {
          cmp = (va ?? 0) - (vb ?? 0);
        } else {
          cmp = (va ?? '').toString().localeCompare((vb ?? '').toString(), undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        }
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  }, [entries, filter, search, sort]);

  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const setField = (key) => (ev) =>
    setForm((f) => ({ ...f, data: { ...f.data, [key]: ev.target.value } }));

  const submit = async (ev) => {
    ev.preventDefault();
    const d = form.data;
    const payload = {
      steamAppId: Number(d.steamAppId),
      name: d.name,
      riskType: d.riskType,
      severity: Number(d.severity),
      source: d.source || null,
      notes: d.notes || null,
      gogUrl: d.gogUrl || null,
    };
    try {
      if (form.mode === 'new') await createRisk(payload);
      else await updateRisk(payload.steamAppId, payload);
      setForm(null);
      await load();
    } catch (err) {
      if (!handleAuthError(err)) {
        setError(err.response?.data?.message || t('admin.error'));
      }
    }
  };

  const remove = async (e) => {
    if (!window.confirm(t('admin.confirm_delete', { name: e.name || e.steamAppId }))) return;
    try {
      await deleteRisk(e.steamAppId);
      await load();
    } catch (err) {
      if (!handleAuthError(err)) setError(t('admin.error'));
    }
  };

  const reimport = async () => {
    try {
      const { data } = await importSeed();
      await load();
      showToast(t('admin.reimported', { count: data.imported }));
    } catch (err) {
      if (!handleAuthError(err)) setError(t('admin.error'));
    }
  };

  const signOut = () => {
    logout();
    setAuthed(false);
  };

  if (!authed) {
    return <AdminLogin onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="admin">
      <header className="admin__header">
        <div>
          <a className="admin__back" href="/">
            {t('admin.back')}
          </a>
          <h1 className="admin__title">
            <span className="brand__mark">◆</span> {t('admin.title')}
          </h1>
          <p className="admin__subtitle">{t('admin.subtitle')}</p>
        </div>
        <div className="admin__header-right">
          <LanguageSwitcher />
          <button className="admin__logout" onClick={signOut}>
            {t('admin.logout')}
          </button>
        </div>
      </header>

      <AdminProToggle onAuthError={handleAuthError} />

      <div className="admin__tabs">
        <button
          type="button"
          className={`admin__tab ${view === 'risks' ? 'is-active' : ''}`}
          onClick={() => setView('risks')}
        >
          {t('admin.tab_risks')}
        </button>
        <button
          type="button"
          className={`admin__tab ${view === 'stats' ? 'is-active' : ''}`}
          onClick={() => setView('stats')}
        >
          {t('admin.tab_dashboard')}
        </button>
        <button
          type="button"
          className={`admin__tab ${view === 'messages' ? 'is-active' : ''}`}
          onClick={() => setView('messages')}
        >
          {t('admin.tab_messages')}
        </button>
        <button
          type="button"
          className={`admin__tab ${view === 'rescue' ? 'is-active' : ''}`}
          onClick={() => setView('rescue')}
        >
          {t('admin.tab_rescue')}
        </button>
        <button
          type="button"
          className={`admin__tab ${view === 'reviews' ? 'is-active' : ''}`}
          onClick={() => setView('reviews')}
        >
          {t('admin.tab_reviews')}
        </button>
      </div>

      {view === 'stats' && <AdminDashboard onAuthError={handleAuthError} />}
      {view === 'messages' && <AdminMessages onAuthError={handleAuthError} />}
      {view === 'rescue' && <AdminRescue onAuthError={handleAuthError} />}
      {view === 'reviews' && <AdminReviews onAuthError={handleAuthError} />}

      {view === 'risks' && (
      <>
      <div className="admin__toolbar">
        <span className="admin__count">{t('admin.count', { count: entries.length })}</span>
        <div className="admin__toolbar-actions">
          <input
            type="search"
            className="admin__search"
            placeholder={t('admin.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="admin__filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">{t('admin.filter_all')}</option>
            {RISK_TYPES.map((rt) => (
              <option key={rt} value={rt}>
                {t(`risk.${rt}`)}
              </option>
            ))}
          </select>
          <button className="btn btn--ghost admin__btn" onClick={reimport}>
            {t('admin.reimport')}
          </button>
          <button
            className="btn btn--amber admin__btn"
            onClick={() => {
              setError(null);
              setForm({ mode: 'new', data: { ...EMPTY } });
            }}
          >
            + {t('admin.add')}
          </button>
        </div>
      </div>

      {toast && <div className="admin__toast">{toast}</div>}
      {error && !form && <div className="admin__error-banner">{error}</div>}

      {loading ? (
        <p className="admin__empty">…</p>
      ) : filtered.length === 0 ? (
        <p className="admin__empty">{t('admin.empty')}</p>
      ) : (
        <div className="admin__table-wrap">
          <table className="admin__table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className={`admin__th admin__th--sortable ${sort.key === c.key ? 'is-active' : ''}`}
                    onClick={() => toggleSort(c.key)}
                  >
                    {t(`admin.${c.label}`)}
                    <span className="admin__sort-ind">
                      {sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
                    </span>
                  </th>
                ))}
                <th>{t('admin.col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.steamAppId}>
                  <td className="admin__mono">{e.steamAppId}</td>
                  <td>{e.name}</td>
                  <td>
                    <span className={`risk-badge risk-badge--${e.riskType}`}>
                      {t(`risk.${e.riskType}`)}
                    </span>
                  </td>
                  <td className="admin__mono">{e.severity}</td>
                  <td>
                    {e.gogUrl ? (
                      <a href={e.gogUrl} target="_blank" rel="noreferrer" className="admin__gog">
                        GOG →
                      </a>
                    ) : (
                      <span className="admin__dash">—</span>
                    )}
                  </td>
                  <td className="admin__notes">{e.notes}</td>
                  <td className="admin__actions">
                    <button
                      className="admin__link"
                      onClick={() => {
                        setError(null);
                        setForm({ mode: 'edit', data: { ...EMPTY, ...e } });
                      }}
                    >
                      {t('admin.edit')}
                    </button>
                    <button className="admin__link admin__link--danger" onClick={() => remove(e)}>
                      {t('admin.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {form && (
        <RiskForm
          mode={form.mode}
          data={form.data}
          onField={setField}
          onSubmit={submit}
          onCancel={() => setForm(null)}
          error={error}
        />
      )}
    </div>
  );
}

export default AdminPage;
